import {
  Box,
  Button,
  FormControl,
  InputLabel,
  OutlinedInput,
  Stack,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router';

import { EmployeeDetails } from './components/EmployeeDetails';
import { EmployeeTable } from './components/EmployeeTable';
import { useColumns } from './components/EmployeeTable/hooks/useColumns';
import { EMPLOYEE_ID_PARAMETER_NAME } from './constants';
import {
  columnVisibilityRequirements,
  defaultCardFieldsVisibility,
  defaultTableColumnVisibility,
  EMPLOYEES_SORT_FIELD_MAP,
  EMPLOYEES_STORAGE_KEYS,
  employeesConfig,
} from './employees.config';
import styles from './EmployeesPortfolio.module.scss';
import { useEmployeesFilterViewStore } from './useEmployeesFilterViewStore';
import { useGetConfigurationFeatures } from '@/api/REST/configuration-features';
import { Employee } from '@/api/typesense/employees';
import { FilterBar } from '@/components/filters';
import { Views } from '@/components/filters/Views';
import { PageTitle } from '@/components/global/PageTitle';
import { Panels } from '@/components/layouts/Panels';
import { Icon } from '@/components/ui/Icon';
import { IconButton } from '@/components/ui/IconButton';
import { PageHeader } from '@/components/ui/PageHeader';
import { ViewOptionsPopover } from '@/components/ui/ViewOptionsPopover';
import { areaIcons } from '@/constants/areaIcons';
import { legacyRoutes } from '@/constants/legacyRoutes';
import { uiIcons } from '@/constants/uiIcons';
import { useSearchResults, useFilterManager } from '@/hooks/filters';
import {
  useFilteredColumns,
  useOrderedColumns,
  useTablePreferences,
  useViewMode,
} from '@/hooks/grid';
import { usePanelDrawer, usePopover, useSearchInput } from '@/hooks/ui';
import { useHasPermissionToAccessPage } from '@/hooks/useHasPermissionToAccessPage';
import type { FilterClause } from '@/lib/filter-dsl';

export const EmployeesPortfolio = () => {
  const { t } = useTranslation('employees');
  const [searchParams] = useSearchParams();
  const selectedEmployeeId = searchParams.get(EMPLOYEE_ID_PARAMETER_NAME);
  const { searchInput, setSearchInput, debouncedSearchInput } =
    useSearchInput();

  const {
    isPanelOpen,
    expandDrawer,
    drawerRef,
    openPanel,
    openPanelAndFocus,
    closePanel,
    handleEscapeFromDrawer,
  } = usePanelDrawer({ selectedId: selectedEmployeeId });

  const {
    filterSet,
    removeFilter,
    updateFilter,
    filterViews,
    currentViewId,
    currentView,
    filterPanelOpen,
    selectView,
    saveCurrentView,
    saveAsNewView,
    createEmptyView,
    resetFilters,
    clearViewAndFilterSelection,
    toggleFilterPanel,
    hasUnsavedChanges,
    hasFilters,
    isSaving,
    isTransitioning,
    isCreatingView,
    isSavingAsNewView,
    startCreatingView,
    endCreatingView,
    startSavingAsNewView,
    endSavingAsNewView,
    duplicateView,
    createViewMutation,
    updateViewMutation,
    deleteViewMutation,
  } = useFilterManager({
    useStore: useEmployeesFilterViewStore,
  });

  // View mode state (kept for when cards view is ready)
  const { viewMode } = useViewMode(EMPLOYEES_STORAGE_KEYS.viewMode);

  const {
    tableSortModel,
    cardsSortModel,
    tableColumnVisibility,
    cardFieldsVisibility,
    handleTableColumnToggle,
    handleCardFieldsToggle,
    columnWidths,
    columnOrder,
    density,
    setDensity,
    setTableColumnVisibility,
    handleColumnWidthChange,
    handleColumnOrderChange,
    setTableSortModel,
  } = useTablePreferences(EMPLOYEES_STORAGE_KEYS, {
    tableColumnVisibility: defaultTableColumnVisibility,
    tableSort: [{ field: 'Name', sort: 'asc' }],
    cardFieldsVisibility: defaultCardFieldsVisibility,
    cardsSort: [{ field: 'Name', sort: 'asc' }],
  });

  const activeSortModel =
    viewMode === 'table' ? tableSortModel : cardsSortModel;
  const activeColumnVisibility =
    viewMode === 'table' ? tableColumnVisibility : cardFieldsVisibility;
  const activeHandleColumnToggle =
    viewMode === 'table' ? handleTableColumnToggle : handleCardFieldsToggle;

  const sortFieldConfig = Object.entries(EMPLOYEES_SORT_FIELD_MAP).map(
    ([field, sortField]) => ({ field, sortField }),
  );

  const { data, isLoading, hasNextPage, fetchNextPage } =
    useSearchResults<Employee>({
      config: employeesConfig,
      filterSet,
      sortModel: activeSortModel,
      sortFieldMappings: sortFieldConfig,
      query: debouncedSearchInput,
    });

  const allEmployees = data?.pages?.flatMap((page) => page.documents) || [];
  const totalResults = data?.pages?.[0]?.totalResults || 0;

  const handleAddFilter = (clause: FilterClause) => {
    updateFilter(clause);
  };

  const viewOptions = usePopover({ id: 'employees-portfolio-view-options' });

  const { data: features, isLoading: isFeaturesLoading } =
    useGetConfigurationFeatures();

  // Same gate as the Create menu in the top navigation (see
  // createMenuItems.ts → name: 'employees'): the user must have the
  // `NewEmployee` page in their permittedPages.
  const { hasPermission: canCreateEmployee } =
    useHasPermissionToAccessPage('NewEmployee');

  const allColumns = useColumns({ columnWidths });

  const filteredColumns = useFilteredColumns({
    columns: allColumns,
    features,
    requirements: columnVisibilityRequirements,
  });

  const columns = useOrderedColumns(filteredColumns, columnOrder);

  return (
    <>
      <PageTitle title={t('PageTitle')} />
      <Panels
        autoSaveId="employees-portfolio-panels"
        expandDrawer={expandDrawer}
        defaultLayoutRatio="2:1"
        collapseDrawerOnEscape
        onDrawerCollapse={closePanel}
        drawerRef={drawerRef}
        onEscapeFromDrawer={handleEscapeFromDrawer}
      >
        <Panels.Main
          panelContentProps={{
            hideOverflow: viewMode === 'table',
            hasPaddingBottom: viewMode === 'cards',
          }}
        >
          <PageHeader title={t('PageTitle')} iconName={areaIcons.employees}>
            {canCreateEmployee && (
              <PageHeader.Info>
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  startIcon={<Icon name={uiIcons.actions.add} />}
                  component={Link}
                  to={legacyRoutes.organization.employee.new}
                  data-automation-id="employees-portfolio-new-employee-button"
                >
                  {t('details.NewEmployeeButton')}
                </Button>
              </PageHeader.Info>
            )}
          </PageHeader>

          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="start"
            gap={2}
          >
            <Views
              filterViews={filterViews}
              createView={createViewMutation}
              createEmptyView={createEmptyView}
              saveAsNewView={saveAsNewView}
              duplicateView={duplicateView}
              updateView={updateViewMutation}
              deleteViewMutation={deleteViewMutation}
              currentViewId={currentViewId}
              hasUnsavedChanges={hasUnsavedChanges}
              onViewSelect={(view) => {
                if (view?.filterViewId) {
                  selectView(view.filterViewId, view.filterParameters);
                } else {
                  clearViewAndFilterSelection();
                }
              }}
              isCreatingView={isCreatingView}
              isSavingAsNewView={isSavingAsNewView}
              onCreateStart={startCreatingView}
              onCreateEnd={endCreatingView}
              onSaveAsNewEnd={endSavingAsNewView}
              filterPanelOpen={filterPanelOpen}
              onToggleFilterPanel={toggleFilterPanel}
            />

            <Stack direction="row" alignItems="center" gap={1}>
              <FormControl>
                <div>
                  <InputLabel visuallyHidden htmlFor="search-input">
                    {t('SearchLabel', { ns: 'ui' })}
                  </InputLabel>
                </div>
                <OutlinedInput
                  id="search-input"
                  type="search"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder={t('SearchLabel', { ns: 'ui' })}
                  pilled
                  size="small"
                  data-automation-id="employees-portfolio-search-input"
                  className={styles.SearchInput}
                />
              </FormControl>

              <IconButton
                size="small"
                color="secondary"
                tooltip={t('tooltip.ViewOptions')}
                iconName={uiIcons.controls.columnPicker}
                variant="outlined"
                onClick={viewOptions.handleOpen}
                aria-haspopup="dialog"
                aria-expanded={viewOptions.open ? 'true' : undefined}
                aria-controls={viewOptions.open ? viewOptions.id : undefined}
                data-automation-id="employees-portfolio-view-options-button"
              />

              <ViewOptionsPopover
                id={viewOptions.id}
                open={viewOptions.open}
                anchorEl={viewOptions.anchorEl}
                onClose={viewOptions.handleClose}
                columns={columns}
                columnVisibilityModel={activeColumnVisibility}
                onColumnToggle={activeHandleColumnToggle}
                density={density}
                onDensityChange={setDensity}
              />
            </Stack>
          </Stack>

          <FilterBar
            id="filter-panel"
            aria-expanded={filterPanelOpen}
            hidden={!filterPanelOpen}
            config={employeesConfig}
            filterSet={filterSet}
            searchQuery={debouncedSearchInput}
            onAddFilter={handleAddFilter}
            onRemoveFilter={removeFilter}
            onUpdateFilter={updateFilter}
            currentView={currentView}
            hasUnsavedChanges={hasUnsavedChanges}
            hasFilters={hasFilters}
            isSaving={isSaving}
            isTransitioning={isTransitioning}
            onSaveCurrentView={saveCurrentView}
            onSaveAsNewView={startSavingAsNewView}
            onResetFilters={resetFilters}
          />

          <Box sx={{ flex: 1, minHeight: 0 }}>
            <EmployeeTable
              items={allEmployees}
              isLastPage={!hasNextPage}
              showMore={fetchNextPage}
              columns={columns}
              columnVisibilityModel={tableColumnVisibility}
              onColumnVisibilityModelChange={setTableColumnVisibility}
              columnWidths={columnWidths}
              onColumnWidthChange={handleColumnWidthChange}
              onColumnOrderChange={handleColumnOrderChange}
              sortModel={tableSortModel}
              onSortModelChange={setTableSortModel}
              density={density}
              loading={isLoading || isFeaturesLoading}
              isPanelOpen={isPanelOpen}
              onPanelOpen={openPanel}
              onPanelOpenAndFocus={openPanelAndFocus}
              totalResults={totalResults}
            />
          </Box>
        </Panels.Main>
        <Panels.Drawer>
          <EmployeeDetails />
        </Panels.Drawer>
      </Panels>
    </>
  );
};
